import i18next, { i18n, t } from 'i18next';
import moment from 'moment/moment';
import tinycolor from 'tinycolor2';
import { formatPhoneNumberIntl } from 'react-phone-number-input';

import { COLOR_BRIGHTNESS_THRESHOLD, COUNTRY_LIST } from '../constants';
import { ITextSpan } from '../types';

import { getCountryName } from './countryUtil';

const DATE_LONG_OPTIONS = { day: '2-digit', month: 'long', year: 'numeric' } as Intl.DateTimeFormatOptions;
const DATE_SHORT_OPTIONS = { day: '2-digit', month: '2-digit' } as Intl.DateTimeFormatOptions;
const TIME_SHORT_OPTIONS = { hour: '2-digit', minute: '2-digit' } as Intl.DateTimeFormatOptions;

// Masking
const DEFAULT_MASKED_LENGTH = 8;
const DEFAULT_VISIBLE_LENGTH = 2;
const MASK_CHARACTER = '•';
export const EMPTY_VALUE = '—';

export const NO_BREAK_SPACE = '\u00A0';

export const equalsIgnoreCase = (x: string, y: string): boolean => x.toLowerCase() === y.toLowerCase();

export const isWhiteSpace = (value: string): boolean => value.trim().length === 0;

export const includesIgnoreCase = (value: string | null | undefined, query: string): boolean =>
  (value || '').toLowerCase().includes(query.toLowerCase());

export const indexOfIgnoreCase = (value: string | null | undefined, query: string, position?: number): number =>
  (value || '').toLowerCase().indexOf(query.toLowerCase(), position);

export const findAllIgnoreCase = (value: string | null | undefined, query: string, position?: number): ITextSpan[] => {
  const results: ITextSpan[] = [];
  if (!value || !query) {
    return results;
  }

  let start = position ?? 0;
  while (start < value.length) {
    const index = indexOfIgnoreCase(value, query, start);
    if (index < 0) {
      results.push({
        found: false,
        value: value.substr(start),
        index: start,
        length: value.length - start,
      });
      return results;
    }

    if (index > start) {
      results.push({
        found: false,
        value: value.substr(start, index - start),
        index: start,
        length: index - start,
      });
    }

    results.push({
      found: true,
      value: value.substr(index, query.length),
      index,
      length: query.length,
    });
    start = index + query.length;
  }

  return results;
};

export const round = (number: number | undefined, digits: number): number => parseFloat(number?.toFixed(digits) || '0');

export const getIgnoreCaseStringComparer = <T>(mapper: (v: T) => string) => (a: T, b: T): number => {
  const mappedA = mapper(a);
  const mappedB = mapper(b);

  if (mappedA.toLowerCase() < mappedB.toLowerCase()) return -1;
  if (mappedA.toLowerCase() > mappedB.toLowerCase()) return 1;
  return 0;
};

export const onlyUnique = (s: string, i: number, a: string[]): boolean => a.indexOf(s) === i;

export const formatDateFull = (dateTime: string | null | undefined, locale: string): string => {
  const date = dateTime ? new Date(dateTime) : undefined;
  return date?.toLocaleDateString([locale]) || EMPTY_VALUE;
};

export const formatTimeShort = (dateTime: string | null | undefined, locale: string): string => {
  const date = dateTime ? new Date(dateTime) : undefined;
  return date?.toLocaleTimeString([locale], TIME_SHORT_OPTIONS) || EMPTY_VALUE;
};

export const formatDateTimeLong = (dateString: string | null | undefined, locale: string): string => {
  const date = dateString ? new Date(dateString) : undefined;
  return date
    ? `${date.toLocaleDateString([locale], DATE_LONG_OPTIONS)} - ${date.toLocaleTimeString([locale])}`
    : EMPTY_VALUE;
};

export const formatDateTimeFull = (dateString: string | null | undefined, locale: string): string => {
  const date = dateString ? new Date(dateString) : undefined;
  return date ? `${date.toLocaleDateString([locale])} - ${date.toLocaleTimeString([locale])}` : EMPTY_VALUE;
};

export const formatDateTimeShort = (dateString: string | null | undefined, locale: string): string => {
  const date = dateString ? new Date(dateString) : undefined;
  return date
    ? `${date.toLocaleDateString([locale])} ${date.toLocaleTimeString([locale], TIME_SHORT_OPTIONS)}`
    : EMPTY_VALUE;
};

export const formatDateTimeAdaptive = (
  dateTime: string | null | undefined,
  locale: string,
  weekday: 'long' | 'short',
  alwaysShowDateAndTime?: boolean
): string => {
  const msPerDay = 24 * 60 * 60 * 1000;
  const msPerWeek = 7 * msPerDay;
  const date = dateTime ? new Date(dateTime) : undefined;
  const lastMidnight = new Date(new Date().setHours(0, 0, 0, 0));
  const secondLastMidnight = new Date(lastMidnight.getTime() - msPerDay);
  const weekAgoMidnight = new Date(lastMidnight.getTime() - msPerWeek);

  if (alwaysShowDateAndTime) {
    const formatTime = () => date?.toLocaleTimeString([locale], TIME_SHORT_OPTIONS);
    return date
      ? date >= lastMidnight
        ? `${i18next.t('Today')}, ${formatTime()}`
        : date >= secondLastMidnight
        ? `${i18next.t('Yesterday')}, ${formatTime()}`
        : date >= weekAgoMidnight
        ? `${date.toLocaleDateString([locale], { weekday })}, ${formatTime()}`
        : `${date.toLocaleDateString([locale], DATE_SHORT_OPTIONS)}, ${formatTime()}`
      : '';
  }

  return date
    ? date >= lastMidnight
      ? date.toLocaleTimeString([locale], TIME_SHORT_OPTIONS)
      : date >= weekAgoMidnight
      ? date.toLocaleDateString([locale], { weekday })
      : date.toLocaleDateString([locale], DATE_SHORT_OPTIONS)
    : '';
};

export const formatDateTimeIso = (
  dateStr: string | null | undefined,
  timeStr: string | null | undefined
): string | undefined => {
  if (!dateStr) {
    return undefined;
  }
  if (!timeStr) {
    timeStr = '00:00';
  }

  return moment(`${dateStr} ${timeStr}`, 'DD.MM.YYYY HH:mm').toISOString();
};

export const utcTimeToLocal = (utcTime: string, outputFormat = 'HH:mm:ss'): string =>
  moment.utc(utcTime, 'HH:mm:ss').local().format(outputFormat);

export const localTimeToUtc = (localTime: string, outputFormat = 'HH:mm:ss'): string =>
  moment(localTime, 'HH:mm:ss').utc().format(outputFormat);

export const formatPhoneNumber = (e164String: string | null | undefined): string => {
  return e164String ? formatPhoneNumberIntl(e164String) : EMPTY_VALUE;
};

export const formatCountry = (country: string | null | undefined, i18n: i18n): string => {
  if (!country) return '';

  const foundCountry = COUNTRY_LIST.find((c) => equalsIgnoreCase(c.alpha2, country));
  return foundCountry ? getCountryName(foundCountry, i18n) : country;
};

export const formatLocation = (
  country: string | null | undefined,
  city: string | null | undefined,
  i18n: i18n
): string => {
  const formattedCountry = formatCountry(country, i18n);

  if (formattedCountry && city) {
    return `${formattedCountry}, ${city}`;
  }

  return formattedCountry || city || EMPTY_VALUE;
};

export const formatFullName = (
  lastName?: string | null,
  firstName?: string | null,
  middleName?: string | null
): string => {
  const lastNameT = (lastName || '').trim();
  const firstNameT = (firstName || '').trim();
  const middleNameT = (middleName || '').trim();

  if (lastNameT && firstNameT && middleNameT) {
    return `${lastNameT} ${firstNameT} ${middleNameT}`;
  }

  if (lastNameT && firstNameT) {
    return `${lastNameT} ${firstNameT}`;
  }

  return lastNameT;
};

export const formatShortName = (
  lastName?: string | null,
  firstName?: string | null,
  middleName?: string | null
): string => {
  const lastNameT = (lastName || '').trim();
  const firstNameT = (firstName || '').trim();
  const middleNameT = (middleName || '').trim();

  if (lastNameT && firstNameT && middleNameT) {
    return `${lastNameT} ${firstNameT.substring(0, 1)}. ${middleNameT.substring(0, 1)}.`;
  }

  if (lastNameT && firstNameT) {
    return `${lastNameT} ${firstNameT.substring(0, 1)}.`;
  }

  return lastNameT;
};

export const isLightColor = (color: string): boolean => tinycolor(color).getBrightness() > COLOR_BRIGHTNESS_THRESHOLD;

export const getInitials = (text: string): string => {
  const splittedText = text.split(' ');
  return `${splittedText[0]?.charAt(0).toUpperCase() || ''}${splittedText[1]?.charAt(0).toUpperCase() || ''}`;
};

export enum MaskingMode {
  Default = 0,
  Length = 1,
  Begin = 2,
  End = 4,
  BeginEnd = Begin | End,
}

export const mask = (
  text: string | null | undefined,
  mode: MaskingMode = MaskingMode.Default,
  maskedLength: number = DEFAULT_MASKED_LENGTH,
  visibleLength: number = DEFAULT_VISIBLE_LENGTH
): string => {
  if (mode === MaskingMode.Default) {
    return MASK_CHARACTER.repeat(maskedLength);
  }

  text = text ?? '';
  const length = text.length;

  if (mode === MaskingMode.Length) {
    return MASK_CHARACTER.repeat(length);
  }

  const beginVisibleLength = (mode & MaskingMode.Begin) === MaskingMode.Begin ? Math.min(visibleLength, length) : 0;

  const endVisibleLength =
    (mode & MaskingMode.End) === MaskingMode.End
      ? Math.min(visibleLength, Math.max(length - beginVisibleLength, 0))
      : 0;

  let result = '';
  if (beginVisibleLength > 0) {
    result += text.slice(0, beginVisibleLength);
  }

  const hiddenLength =
    (mode & MaskingMode.Length) === MaskingMode.Length ? length - beginVisibleLength - endVisibleLength : maskedLength;
  result += MASK_CHARACTER.repeat(Math.max(hiddenLength, 0));

  if (endVisibleLength > 0) {
    result += text.slice(length - endVisibleLength);
  }

  return result;
};

export const calculateSimpleDigest = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // NOTE: Convert to 32-bit integer.
  }
  return hash.toString(36);
};

export const getTranslateFunc = (prefix: string): ((key: string, params?: { [key: string]: unknown }) => string) => {
  return (key, params) => {
    return t(`${prefix} ${key}`, params);
  };
};

export const isValidJSON = (value?: string, options?: { allowEmpty?: boolean; objectOnly?: boolean }): boolean => {
  const jsonValue = value?.trim();
  if (!jsonValue) {
    return !!options?.allowEmpty;
  }

  try {
    const parsed = JSON.parse(jsonValue);
    return !options?.objectOnly || (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed));
  } catch {
    return false;
  }
};

export const splitLines = (value: string): string[] =>
  value
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);
